/**
 * PersonaPicker — tier-grouped grid of AI council members with bulk
 * select / clear / per-tier "add all" controls and a {selected}/{cap}
 * counter. Pure: emits `onToggle(id)` per card click and `onBulk({...})`
 * for the select-all / clear / select-tier actions.
 */
import React, { useMemo } from 'react';
import { Sparkles, Check, Users2, XCircle } from 'lucide-react';
import { ICON_MAP, TIER_LABEL, TIER_ORDER, COUNCIL_HARD_CAP } from './_constants';

export default function PersonaPicker({ personas, selected, onToggle, onSelectAll, onClear, onSelectTier }) {
  const grouped = useMemo(() => {
    const out = {};
    for (const p of personas) {
      const t = p.tier || 'leadership';
      (out[t] ||= []).push(p);
    }
    return out;
  }, [personas]);

  return (
    <div className="mt-8 ve-card p-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="display text-lg font-bold">
          1. Pick your council ({selected.length}/{COUNCIL_HARD_CAP})
        </h2>
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="council-select-all"
            onClick={onSelectAll}
            className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md border border-[#8B3FC5] bg-[#FFF8E5] text-[#7A5A00] dark:bg-[#3a2f0a] dark:text-[#F5E1A4] text-[10px] uppercase tracking-[0.18em] font-bold hover:bg-[#8B3FC5] hover:text-white transition"
          >
            <Users2 size={12}/> Select all {personas.length}
          </button>
          <button
            data-testid="council-clear"
            onClick={onClear}
            disabled={!selected.length}
            className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md border border-slate-200 dark:border-white/10 text-slate-600 dark:text-white/70 text-[10px] uppercase tracking-[0.18em] font-bold hover:border-rose-400 hover:text-rose-500 transition disabled:opacity-40"
          >
            <XCircle size={12}/> Clear
          </button>
        </div>
      </div>

      {TIER_ORDER.filter((t) => grouped[t]?.length).map((tier) => (
        <div key={tier} className="mt-5">
          <div className="flex items-center justify-between mb-2">
            <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5]">{TIER_LABEL[tier]} · {grouped[tier].length}</div>
            <button
              data-testid={`council-select-tier-${tier}`}
              onClick={() => onSelectTier(tier, grouped[tier].map((p) => p.id))}
              className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 dark:text-white/55 hover:text-[#5E1F8B] dark:hover:text-[#8B3FC5] transition"
            >
              + Add all {TIER_LABEL[tier].toLowerCase()}
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {grouped[tier].map((p) => {
              const Icon = ICON_MAP[p.icon] || Sparkles;
              const isSel = selected.includes(p.id);
              return (
                <button
                  key={p.id}
                  onClick={() => onToggle(p.id)}
                  data-testid={`council-pick-${p.id}`}
                  className={`rounded-lg border p-3 text-left transition-all ${
                    isSel
                      ? 'border-[#8B3FC5] bg-[#FFF8E5] dark:bg-[#3a2f0a] shadow-[0_0_25px_-12px_rgba(174,82,234,0.6)]'
                      : 'border-slate-200 dark:border-white/10 hover:border-[#8B3FC5]/60 hover:bg-[#FFF8E5]/30 dark:hover:bg-white/[0.03]'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="h-9 w-9 rounded-full border border-[#8B3FC5]/40 bg-black flex items-center justify-center shrink-0">
                        <Icon size={16} className="text-[#8B3FC5]"/>
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-bold leading-tight truncate" title={p.english_name}>{p.english_name}</div>
                        <div className="text-[15px] text-[#8B3FC5] font-bold leading-tight truncate" dir="rtl" title={p.arabic_name}>{p.arabic_name}</div>
                      </div>
                    </div>
                    {isSel && <Check size={16} className="text-[#5E1F8B] shrink-0"/>}
                  </div>
                  <div className="mt-2 text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-white/55 font-bold truncate" title={p.english_role}>
                    {p.english_role}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
